/**
 * Outil « Machine à nombres » (Boîte à outils, 6e → 3e).
 *
 * Un programme de calcul en étapes, qu'on choisit ou qu'on construit. « Un
 * nombre » : le nombre passe dans chaque machine (fractions exactes), et on
 * peut remonter le programme pour retrouver le nombre de départ. « Avec x »
 * (dès la 5e) : on remplace le nombre de départ par x, chaque étape s'écrit
 * puis se réduit — le passage au calcul littéral. « Deux programmes » (dès la
 * 4e) : mêmes formes réduites = même résultat pour TOUS les nombres, ce que
 * des essais ne prouvent pas. Calculs : `machine.ts`.
 */

import { useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  IconButton,
  Note,
  NumberField,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
} from '../../ui/controls';
import { exact, frac, fromDecimal, type Frac } from '../../ui/exact';
import {
  compare,
  evalPoly,
  MAX_STEPS,
  polyAtoms,
  polyPlain,
  polyTex,
  PROGRAMS,
  reverse,
  runNumber,
  runPoly,
  stepLabel,
  stepShort,
  stepTex,
  fits,
  safeFrac,
  usesSquare,
  type Poly,
  type Step,
} from './machine';

type Mode = 'number' | 'letter' | 'compare';

/** étapes qu'on peut ajouter : opération + opérande */
const KINDS = [
  { id: 'add', label: 'Ajouter un nombre', value: true },
  { id: 'sub', label: 'Soustraire un nombre', value: true },
  { id: 'mul', label: 'Multiplier par un nombre', value: true },
  { id: 'div', label: 'Diviser par un nombre', value: true },
  { id: 'square', label: 'Élever au carré', value: false },
  { id: 'addX', label: 'Ajouter k fois le nombre de départ', value: true },
  { id: 'subX', label: 'Soustraire k fois le nombre de départ', value: true },
  { id: 'mulX', label: 'Multiplier par le nombre de départ', value: false },
  { id: 'subX2', label: 'Soustraire le carré du nombre de départ', value: false },
] as const;
type Kind = (typeof KINDS)[number]['id'];

function makeStep(kind: Kind, k: number): Step {
  switch (kind) {
    case 'add':
    case 'sub':
    case 'mul':
      return { op: kind, operand: { kind: 'const', k } };
    case 'div':
      return { op: 'div', k };
    case 'square':
      return { op: 'square' };
    case 'addX':
      return { op: 'add', operand: { kind: 'x', k } };
    case 'subX':
      return { op: 'sub', operand: { kind: 'x', k } };
    case 'mulX':
      return { op: 'mul', operand: { kind: 'x', k: 1 } };
    case 'subX2':
      return { op: 'sub', operand: { kind: 'x2' } };
  }
}

const valueTex = (f: Frac) => exact(f).tex;

export function NumberMachineTool({
  onSend,
  sendCost,
  level,
}: ToolProps & { level?: string | null }) {
  const rank = schoolLevelRank(level);
  const letterOk = rank === null || rank >= 2;
  // carrés (développer (x + 1)²) et comparaison de programmes : 4e
  const squareOk = rank === null || rank >= 3;
  const compareOk = squareOk;
  const relativeOk = rank === null || rank >= 2;
  const programs = PROGRAMS.filter((p) => squareOk || !p.steps.some(usesSquare));
  const [mode, setMode] = useState<Mode>('number');
  const [preset, setPreset] = useState('double');
  const [steps, setSteps] = useState<Step[]>([...PROGRAMS[0]!.steps]);
  const [start, setStart] = useState(5);
  const [target, setTarget] = useState(20);
  const [kind, setKind] = useState<Kind>('add');
  const [k, setK] = useState(2);
  const [other, setOther] = useState('twoplus');
  const [refused, setRefused] = useState(false);

  const x0 = fromDecimal(start);
  const raw = runNumber(steps, x0);
  // au-delà des entiers exacts, on n'affiche plus de nombre (jamais « 1e+24 »)
  const firstUnsafe = raw.findIndex((f) => !safeFrac(f));
  const values = raw.map((f, i) => (firstUnsafe !== -1 && i >= firstUnsafe ? null : f));
  const polys = runPoly(steps);
  const final = polys[polys.length - 1]!;
  const result = values[values.length - 1] ?? null;
  const kindDef = KINDS.find((d) => d.id === kind)!;

  const choose = (id: string) => {
    setPreset(id);
    setSteps([...PROGRAMS.find((p) => p.id === id)!.steps]);
  };
  const edit = (next: Step[]) => {
    setPreset('');
    setSteps(next);
    setRefused(false);
  };
  const addStep = () => {
    const next = [...steps, makeStep(kind, k)];
    if (!fits(next)) {
      setRefused(true);
      return;
    }
    edit(next);
  };

  const modes = [
    { value: 'number' as const, label: 'Un nombre' },
    ...(letterOk ? [{ value: 'letter' as const, label: 'Avec x' }] : []),
    ...(compareOk ? [{ value: 'compare' as const, label: 'Deux programmes' }] : []),
  ];

  return (
    <div className="space-y-4">
      {modes.length > 1 && (
        <Segmented ariaLabel="Mode" value={mode} options={modes} onChange={setMode} />
      )}
      {mode === 'compare' && (
        <p className="text-ink-500 -mb-2 text-xs font-semibold uppercase tracking-wide">
          Programme A
        </p>
      )}
      <Segmented
        ariaLabel="Programme"
        value={preset}
        options={programs.map((p) => ({ value: p.id, label: p.label }))}
        onChange={choose}
      />

      {mode === 'number' && (
        <NumberField
          label="Nombre de départ"
          value={start}
          min={relativeOk ? -1000 : 0}
          max={1000}
          decimals={2}
          onChange={setStart}
        />
      )}

      <ol className="space-y-1" aria-label="Programme de calcul" data-testid="nm-steps">
        <li className="flex items-center gap-2">
          <Box>
            {mode === 'number' ? (
              <MathText>{`$${valueTex(x0)}$`}</MathText>
            ) : (
              <MathText>{'$x$'}</MathText>
            )}
          </Box>
          <span className="text-ink-500 text-sm">Choisir un nombre</span>
        </li>
        {steps.map((s, i) => (
          <li key={i} className="space-y-1">
            <div className="text-ink-600 flex items-center gap-2 pl-3 text-sm">
              <span aria-hidden className="text-primary-500 text-lg leading-none">
                ↓
              </span>
              <span className="font-semibold tabular-nums">{stepShort(s, letterOk)}</span>
              <span className="min-w-0 flex-1">{stepLabel(s)}</span>
              <IconButton
                label={`Retirer l’étape ${i + 1}`}
                onClick={() => edit(steps.filter((_, j) => j !== i))}
              >
                <X className="h-4 w-4" aria-hidden />
              </IconButton>
            </div>
            <div className="flex items-center gap-2" data-testid={`nm-value-${i + 1}`}>
              <Box strong={i === steps.length - 1}>
                {mode === 'number' ? (
                  values[i + 1] ? (
                    <MathText>{`$${valueTex(values[i + 1]!)}$`}</MathText>
                  ) : (
                    <span className="text-ink-600 text-sm">trop grand</span>
                  )
                ) : (
                  <MathText>{`$${stepTex(polys[i]!, s)}$`}</MathText>
                )}
              </Box>
              {mode !== 'number' && stepTex(polys[i]!, s) !== polyTex(polys[i + 1]!) && (
                <MathText className="text-ink-700 min-w-0 overflow-x-auto">{`$= ${polyTex(polys[i + 1]!)}$`}</MathText>
              )}
            </div>
          </li>
        ))}
      </ol>

      {steps.length < MAX_STEPS && (
        <section
          aria-label="Ajouter une étape"
          className="border-ink-100 space-y-2 rounded-2xl border p-3"
        >
          <label className="block">
            <span className="text-ink-500 block text-xs font-semibold">Nouvelle étape</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as Kind)}
              className="border-ink-200 text-ink-900 mt-1 min-h-11 w-full rounded-xl border bg-transparent px-2 text-sm"
            >
              {KINDS.filter(
                (d) => squareOk || !(d.id === 'square' || d.id === 'mulX' || d.id === 'subX2'),
              ).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {kindDef.value && (
              <div className="min-w-0 flex-1">
                <Stepper
                  label={kind.endsWith('X') ? 'k' : 'Nombre'}
                  value={k}
                  min={1}
                  max={20}
                  onChange={setK}
                />
              </div>
            )}
            <button type="button" className={SECONDARY_BUTTON} onClick={addStep}>
              Ajouter l’étape
            </button>
          </div>
          {refused && (
            <p className="text-ink-700 text-sm" role="status">
              Étape refusée : les nombres deviendraient trop grands pour la machine (au plus x⁴).
            </p>
          )}
        </section>
      )}

      {mode === 'number' && (
        <NumberMode
          steps={steps}
          final={final}
          rank={rank}
          relativeOk={relativeOk}
          result={result}
          target={target}
          setTarget={setTarget}
          onSend={onSend}
          sendCost={sendCost}
        />
      )}

      {mode === 'letter' && (
        <section className="space-y-2" aria-live="polite" data-testid="nm-letter">
          <div className="flex flex-wrap items-center gap-2">
            <MathText className="text-ink-900 min-w-0 flex-1 overflow-x-auto text-lg font-bold">{`$\\text{Résultat} = ${polyTex(final)}$`}</MathText>
            {onSend && (
              <SendToSheetButton
                label={polyPlain(final)}
                cost={sendCost}
                onClick={() => onSend({ atoms: polyAtoms(final) })}
              />
            )}
          </div>
          <table
            className="text-ink-700 text-sm tabular-nums"
            aria-label="Vérification avec des nombres"
          >
            <thead>
              <tr>
                <th className="pr-3 text-left font-semibold">Départ</th>
                {[1, 2, 3, 10].map((v) => (
                  <td key={v} className="px-2 text-right">
                    {v}
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th className="pr-3 text-left font-semibold">Résultat</th>
                {[1, 2, 3, 10].map((v) => (
                  <td key={v} className="px-2 text-right">
                    {exact(evalPoly(final, frac(v))).plain}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <Note live={false}>
            Avec x, chaque étape s’écrit à partir de la forme réduite de la précédente, puis se
            réduit. La forme réduite donne le résultat pour n’importe quel nombre de départ.
          </Note>
        </section>
      )}

      {mode === 'compare' && <CompareMode a={final} other={other} setOther={setOther} />}
    </div>
  );
}

function Box({ children, strong = false }: { children: ReactNode; strong?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex min-h-11 min-w-16 max-w-full items-center justify-center overflow-x-auto rounded-xl border px-3 text-lg',
        strong
          ? 'border-primary-500 bg-primary-50 text-ink-900 font-bold'
          : 'border-ink-200 text-ink-900',
      )}
    >
      {children}
    </span>
  );
}

function NumberMode({
  steps,
  final,
  rank,
  relativeOk,
  result,
  target,
  setTarget,
  onSend,
  sendCost,
}: {
  steps: Step[];
  final: Poly;
  rank: number | null;
  relativeOk: boolean;
  result: Frac | null;
  target: number;
  setTarget: (v: number) => void;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const r = result ? exact(result) : null;
  const t = fromDecimal(target);
  const back = reverse(steps, t);
  const equationOk = rank === null || rank >= 3;
  const constant = final[0] ?? frac(0);
  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-testid="nm-result">
        <p className="text-ink-900 min-w-0 flex-1 text-lg font-bold">
          Résultat :{' '}
          {r ? <MathText>{`$${r.tex}$`}</MathText> : 'trop grand pour être écrit exactement'}
        </p>
        {onSend && r && (
          <SendToSheetButton
            label={r.plain}
            cost={sendCost}
            onClick={() => onSend({ atoms: r.atoms })}
          />
        )}
      </div>

      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
        aria-live="polite"
        data-testid="nm-reverse"
      >
        <p className="text-ink-900 text-sm font-semibold">Retrouver le nombre de départ</p>
        <NumberField
          label="Résultat voulu"
          value={target}
          min={relativeOk ? -1000 : 0}
          max={1000}
          decimals={2}
          onChange={setTarget}
        />
        {back.how === 'steps' && (
          <>
            <p className="text-ink-700 text-sm">
              {
                'On remonte le programme avec les opérations réciproques, de la dernière étape à la première\u00a0:'
              }
            </p>
            <p className="text-ink-900 flex flex-wrap items-center gap-x-2 text-sm tabular-nums">
              <span>{exact(fromDecimal(target)).plain}</span>
              {back.chain.map((c, i) => (
                <span key={i}>
                  <span className="text-primary-600 font-semibold">{c.label}</span> →{' '}
                  {exact(c.value).plain}
                </span>
              ))}
            </p>
            <p className="text-ink-900 text-sm font-bold">
              Nombre de départ : {exact(back.start).plain}
            </p>
          </>
        )}
        {back.how === 'equation' &&
          (equationOk ? (
            <p className="text-ink-700 text-sm">
              Le programme réutilise le nombre de départ en cours de route : on ne peut pas le
              remonter étape par étape. Avec x, on résout l’équation « forme réduite ={' '}
              {exact(t).plain} » : le nombre de départ est{' '}
              <strong className="text-ink-900">{exact(back.start).plain}</strong>.
            </p>
          ) : (
            <p className="text-ink-700 text-sm">
              Le programme réutilise le nombre de départ en cours de route : on ne peut pas le
              remonter étape par étape. Essaie des nombres de départ : avec{' '}
              <strong className="text-ink-900">{exact(back.start).plain}</strong>, on obtient bien{' '}
              {exact(t).plain}.
            </p>
          ))}
        {back.how === 'none' && (
          <p className="text-ink-700 text-sm">
            {back.why === 'constant'
              ? constant.p * t.q === t.p * constant.q
                ? `Ce programme donne toujours ${exact(constant).plain} : n’importe quel nombre de départ convient.`
                : `Ce programme donne toujours ${exact(constant).plain} : aucun nombre de départ ne donne ${exact(t).plain}.`
              : back.why === 'square'
                ? 'Avec un carré, deux nombres opposés donnent le même carré : on ne remonte pas « élever au carré » sans précaution.'
                : rank === null || rank >= 5
                  ? 'La forme réduite contient x² : l’équation est du second degré.'
                  : 'La forme réduite contient x² : on ne sait pas encore résoudre cette équation. Essaie des nombres de départ.'}
          </p>
        )}
      </section>
    </>
  );
}

function CompareMode({
  a,
  other,
  setOther,
}: {
  a: Poly;
  other: string;
  setOther: (id: string) => void;
}) {
  const bs = runPoly(PROGRAMS.find((p) => p.id === other)!.steps);
  const b = bs[bs.length - 1]!;
  const verdict = compare(a, b);
  let witness: number | null = null;
  if (verdict.kind === 'other' || verdict.kind === 'never') {
    for (let v = 0; v <= 10 && witness === null; v++) {
      const fa = evalPoly(a, frac(v));
      const fb = evalPoly(b, frac(v));
      if (fa.p * fb.q !== fb.p * fa.q) witness = v;
    }
  }
  return (
    <section className="space-y-3" aria-live="polite" data-testid="nm-compare">
      <div>
        <p className="text-ink-500 mb-1 text-xs font-semibold uppercase tracking-wide">
          Programme B
        </p>
        <Segmented
          ariaLabel="Programme B"
          value={other}
          options={PROGRAMS.map((p) => ({ value: p.id, label: p.label }))}
          onChange={setOther}
        />
      </div>
      <div className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2">
        <MathText className="block overflow-x-auto">{`$A(x) = ${polyTex(a)}$`}</MathText>
        <MathText className="block overflow-x-auto">{`$B(x) = ${polyTex(b)}$`}</MathText>
      </div>
      <Note tone={verdict.kind === 'always' ? 'success' : 'neutral'} live={false}>
        {verdict.kind === 'always' &&
          'Les formes réduites sont égales : les deux programmes donnent le même résultat pour TOUS les nombres de départ. Des essais avec quelques nombres ne le prouvaient pas ; le calcul avec x, si.'}
        {verdict.kind === 'never' &&
          `A(x) − B(x) ne dépend pas de x et n’est pas nul : jamais le même résultat${witness !== null ? ` (avec ${witness} : ${exact(evalPoly(a, frac(witness))).plain} et ${exact(evalPoly(b, frac(witness))).plain})` : ''}.`}
        {verdict.kind === 'once' &&
          `Même résultat pour un seul nombre de départ : on résout A(x) = B(x), x = ${exact(verdict.x).plain}.`}
        {verdict.kind === 'other' &&
          `Formes réduites différentes : pas toujours le même résultat${witness !== null ? ` (avec ${witness} : ${exact(evalPoly(a, frac(witness))).plain} contre ${exact(evalPoly(b, frac(witness))).plain})` : ''}. Un seul contre-exemple suffit à le prouver.`}
      </Note>
    </section>
  );
}
